import { inject } from '@angular/core';
import { ListingsService } from '../listings.service';
import { ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, first, map, of, switchMap } from 'rxjs';
import { Listing } from '../../dto/listing.model';
import { CategoryService } from '../category.service';
import { Category } from '../../dto/category.model';
import { VEHICLE_CONDITIONS } from '../../dto/vehicle-conditions.model';
import { VEHICLE_COLORS } from '../../dto/vehicle-colors.model';
import { VEHICLE_EMISSIONS } from '../../dto/vehicle-emissions.model';
import { VEHICLE_BODY_TYPES } from '../../dto/vehicle-body-types.model';
import { VEHICLE_FUELS } from '../../dto/vehicle-fuel.model';
import { VEHICLE_TRANSMISSIONS } from '../../dto/vehicle-transmissions.model';
import { VEHICLE_SUSPENSIONS } from '../../dto/vehicle-suspension.model';

export const listingResolver: ResolveFn<Listing | RedirectCommand> = (route: ActivatedRouteSnapshot) => {
    const listingService = inject(ListingsService);
    const router = inject(Router);
    const slug = route.paramMap.get('slug')!;

    return listingService.getWithSlug(slug).pipe(
        catchError((error) => {
            console.error('Error fetching listing:', error);
            return of(new RedirectCommand(router.parseUrl('/listings')));
        })
    );
}

export const featuredListingResolver: ResolveFn<Listing[]> = (route: ActivatedRouteSnapshot) => {
    const listingService = inject(ListingsService);

    // Let's go with 6 featured listings for now; mobile will only display 3
    return listingService.getWithFilter(0, 6, { sortOrder: 'desc', sortBy: 'createdAt' }).pipe(
        catchError((error) => {
            console.error('Error fetching listing:', error);
            return of([] as Listing[]);
        })
    );
}

export const browseResolver: ResolveFn<{ listings: Listing[]; total: number, allCategories: Category[], selectedCategory: Category | null }> = (route: ActivatedRouteSnapshot) => {
    const listingService = inject(ListingsService);
    const categoryService = inject(CategoryService);

    // Straightforward single filters
    const model = route.paramMap.get('model');
    const make = route.paramMap.get('make');
    let category = route.paramMap.get('categorySlug');

    // Dynamic parameter filters
    const firstFilter = route.paramMap.get('firstFilter');
    const secondFilter = route.paramMap.get('secondFilter');
    const thirdFilter = route.paramMap.get('thirdFilter');

    // Query filters + pagination and sorting
    const currentPage = route.queryParamMap.get('page') ? Number(route.queryParamMap.get('page')) : 0;
    const sortOrder = route.queryParamMap.get('sortOrder') ? route.queryParamMap.get('sortOrder') : 'desc';
    const sortBy = route.queryParamMap.get('sortBy') ? route.queryParamMap.get('sortBy') : 'createdAt';
    const minPrice = route.queryParamMap.get('minPrice') ? Number(route.queryParamMap.get('minPrice')) : null;
    const maxPrice = route.queryParamMap.get('maxPrice') ? Number(route.queryParamMap.get('maxPrice')) : null;
    const minYear = route.queryParamMap.get('minYear') ? Number(route.queryParamMap.get('minYear')) : null;
    const maxYear = route.queryParamMap.get('maxYear') ? Number(route.queryParamMap.get('maxYear')) : null;
    const minPerformance = route.queryParamMap.get('minPerformance') ? Number(route.queryParamMap.get('minPerformance')) : null;
    const maxPerformance = route.queryParamMap.get('maxPerformance') ? Number(route.queryParamMap.get('maxPerformance')) : null;
    const minFlatbedHeight = route.queryParamMap.get('minFlatbedHeight') ? Number(route.queryParamMap.get('minFlatbedHeight')) : null;
    const maxFlatbedHeight = route.queryParamMap.get('maxFlatbedHeight') ? Number(route.queryParamMap.get('maxFlatbedHeight')) : null;
    const minMileage = route.queryParamMap.get('minMileage') ? Number(route.queryParamMap.get('minMileage')) : null;
    const maxMileage = route.queryParamMap.get('maxMileage') ? Number(route.queryParamMap.get('maxMileage')) : null;
    const minWeight = route.queryParamMap.get('minWeight') ? Number(route.queryParamMap.get('minWeight')) : null;
    const maxWeight = route.queryParamMap.get('maxWeight') ? Number(route.queryParamMap.get('maxWeight')) : null;
    const minUsageWeight = route.queryParamMap.get('minUsageWeight') ? Number(route.queryParamMap.get('minUsageWeight')) : null;
    const maxUsageWeight = route.queryParamMap.get('maxUsageWeight') ? Number(route.queryParamMap.get('maxUsageWeight')) : null;
    const minTrailorWeight = route.queryParamMap.get('minTrailorWeight') ? Number(route.queryParamMap.get('minTrailorWeight')) : null;
    const maxTrailorWeight = route.queryParamMap.get('maxTrailorWeight') ? Number(route.queryParamMap.get('maxTrailorWeight')) : null;

    const conditionAsString = route.queryParamMap.get('condition') ? route.queryParamMap.get('condition') : null;
    const condition = VEHICLE_CONDITIONS.find(c => c.name.toLowerCase() === conditionAsString?.toLowerCase())?.id ?? null;

    const suspensionAsString = route.queryParamMap.get('suspension') ? route.queryParamMap.get('suspension') : null;
    const suspension = VEHICLE_SUSPENSIONS.find(s => s.name.toLowerCase() === suspensionAsString?.toLowerCase())?.id ?? null;

    const colorAsString = route.queryParamMap.get('color') ? route.queryParamMap.get('color') : null;
    const color = VEHICLE_COLORS.find(c => c.name.toLowerCase() === colorAsString?.toLowerCase())?.id ?? null;

    const transmissionAsString = route.queryParamMap.get('transmission') ? route.queryParamMap.get('transmission') : null;
    const transmission = VEHICLE_TRANSMISSIONS.find(t => t.name.toLowerCase() === transmissionAsString?.toLowerCase())?.id ?? null;

    const fuelTypeAsString = route.queryParamMap.get('fuelType') ? route.queryParamMap.get('fuelType') : null;
    const fuelType = VEHICLE_FUELS.find(f => f.name.toLowerCase() === fuelTypeAsString?.toLowerCase())?.id ?? null;

    const bodyTypeAsString = route.queryParamMap.get('bodyType') ? route.queryParamMap.get('bodyType') : null;
    const bodyType = VEHICLE_BODY_TYPES.find(b => b.name.toLowerCase() === bodyTypeAsString?.toLowerCase())?.id ?? null;

    const emissionAsString = route.queryParamMap.get('emission') ? route.queryParamMap.get('emission') : null;
    const emission = VEHICLE_EMISSIONS.find(e => e.name.toLowerCase() === emissionAsString?.toLowerCase())?.id ?? null;

    const filters: any = {
        sortOrder: sortOrder,
        sortBy: sortBy,
        minPrice: minPrice,
        maxPrice: maxPrice,
        minYear: minYear,
        maxYear: maxYear,
        minPerformance: minPerformance,
        maxPerformance: maxPerformance,
        minFlatbedHeight: minFlatbedHeight,
        maxFlatbedHeight: maxFlatbedHeight,
        minMileage: minMileage,
        maxMileage: maxMileage,
        minWeight: minWeight,
        maxWeight: maxWeight,
        minUsageWeight: minUsageWeight,
        maxUsageWeight: maxUsageWeight,
        minTrailorWeight: minTrailorWeight,
        maxTrailorWeight: maxTrailorWeight,

        condition: condition,
        suspension: suspension,
        color: color,
        transmission: transmission,
        fuel: fuelType,
        bodyType: bodyType,
        emission: emission
    };

    // Figure out model from dynamic url segments
    if (model) {
        filters.model = model;
    }
    if (firstFilter && firstFilter.startsWith('mo-')) {
        filters.model = firstFilter.substring(3);
    }
    if (secondFilter && secondFilter.startsWith('mo-')) {
        filters.model = secondFilter.substring(3);
    }
    if (thirdFilter && thirdFilter.startsWith('mo-')) {
        filters.model = thirdFilter.substring(3);
    }

    // Figure out make from dynamic url segments
    if (make) {
        filters.make = make;
    }
    if (firstFilter && firstFilter.startsWith('mk-')) {
        filters.make = firstFilter.substring(3);
    }
    if (secondFilter && secondFilter.startsWith('mk-')) {
        filters.make = secondFilter.substring(3);
    }
    if (thirdFilter && thirdFilter.startsWith('mk-')) {
        filters.make = thirdFilter.substring(3);
    }

    // Figure out category from dynamic url segments
    if (firstFilter && firstFilter.startsWith('c-')) {
        category = firstFilter.substring(2);
    }
    if (secondFilter && secondFilter.startsWith('c-')) {
        category = secondFilter.substring(2);
    }
    if (thirdFilter && thirdFilter.startsWith('c-')) {
        category = thirdFilter.substring(2);
    }

    // Return the results plus the total count for pagination in one result
    return categoryService.getAll()
        .pipe(
            switchMap(categories => {
                let matchedCategory: Category | null = null;
                if (category) {
                    matchedCategory = categories.find(c => c.slug === category) || null;
                    if (matchedCategory) {
                        filters.categoryId = matchedCategory.id;
                    }
                }

                return listingService.getPageWithFilter(currentPage, 10, filters).pipe(
                    map((page) => ({ listings: page.listings, total: page.total, allCategories: categories, selectedCategory: matchedCategory })),
                    catchError((error) => {
                        console.error('Error fetching listing:', error);
                        return of({ listings: [] as Listing[], total: 0, allCategories: [] as Category[], selectedCategory: null });
                    })
                );
            })
        );
}